import React, { useState } from 'react';
import { useToast } from '../contexts/ToastContext';
import { actasAPI, authAPI } from '../services/api';
import { CheckCircle, XCircle } from 'lucide-react';
import { Eye, Download, Upload, PenTool, Clock } from './icons';
import Tooltip from './Tooltip';
import ReasonModal from './ReasonModal';
import SignatureDrawer from './SignatureDrawer';

const ActaActions = ({ acta, asignacion, movimiento, onActionComplete, onApprove, onReject, currentUserRole }) => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [showSignatureDrawer, setShowSignatureDrawer] = useState(false);
  const [modals, setModals] = useState({ approve: false, reject: false, annul: false });

  // Determinar qué acciones están disponibles según el estado del acta
  const canPreview = true; // Siempre se puede previsualizar
  const canDownload = acta && acta.rutaArchivo;
  // Un usuario (no TI) puede firmar cuando no hay acta o cuando está pendiente/rechazada.
  // Para admin/soporte ocultaremos la opción de firmar; ese control viene desde la página padre vía rol o props.
  const canSign = !acta || acta.estado?.toLowerCase() === 'pendiente' || acta.estado?.toLowerCase() === 'rechazada';
  const canUpload = !acta || acta.estado?.toLowerCase() === 'pendiente' || acta.estado?.toLowerCase() === 'rechazada';
  const canApprove = acta && acta.estado?.toLowerCase() === 'firmada'; // Solo aprobar si está firmada
  const canReject = !!acta; // Se puede rechazar en cualquier estado

  // Función para previsualizar acta según su estado
  const handlePreview = async () => {
    try {
      setLoading(true);
      let response;

      if (!acta) {
        if (asignacion) {
          response = await actasAPI.previsualizarActa(asignacion.id);
        } else if (movimiento) {
          // Usamos la misma lógica que en GestionActas para previsualizar sin acta
          response = await actasAPI.previsualizarActa(null, { movimientoId: movimiento.id });
        }
      } else {
        // Preferimos preview-auto
        try {
          response = await actasAPI.previewAuto(acta.id);
        } catch (e) {
          if (e?.response?.status === 404) {
            if (asignacion) {
              response = await actasAPI.previsualizarActa(asignacion.id);
            } else if (movimiento) {
              response = await actasAPI.previsualizarActa(null, { movimientoId: movimiento.id });
            }
          } else {
            throw e;
          }
        }
      }

      const url = window.URL.createObjectURL(response.data);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error al previsualizar:', error);
      showToast('Error', 'No se pudo previsualizar el acta', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Función para descargar acta
  const handleDownload = async () => {
    try {
      setLoading(true);
      const response = await actasAPI.descargarActa(acta.id);
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      const fileName = acta.nombreArchivo || (asignacion ? `Acta_${asignacion.activo?.codigo}.pdf` : `Acta_Consumible_${movimiento?.id}.pdf`);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showToast('Éxito', 'Acta descargada correctamente', 'success');
    } catch (error) {
      console.error('Error al descargar:', error);
      showToast('Error', 'No se pudo descargar el acta', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Función para firmar digitalmente (oculto para admin/soporte; visible para usuarios)
  const handleDigitalSign = async () => {
    try {
      setLoading(true);
      await actasAPI.firmarDigital({
        asignacionId: asignacion?.id,
        movimientoId: movimiento?.id,
        observaciones: 'Firma digital aplicada',
      });
      showToast('Éxito', 'Acta firmada digitalmente correctamente', 'success');
      if (onActionComplete) onActionComplete();
    } catch (error) {
      console.error('Error al firmar:', error);
      if (error.response?.status === 400) {
        // Si no hay firma en el perfil, ofrecer crearla en el momento
        setShowSignatureDrawer(true);
        const infoMsg = error.response?.data?.message || 'No tienes una firma digital configurada. Crea tu firma ahora.';
        showToast('Información', infoMsg, 'info');
      } else {
        showToast('Error', 'No se pudo firmar el acta', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  // Guardar firma dibujada y reintentar firma digital
  const handleSaveSignature = async (file) => {
    try {
      setLoading(true);
      const fd = new FormData();
      fd.append('signature', file);
      await authAPI.uploadSignature(fd);
      showToast('Éxito', 'Tu firma se guardó correctamente', 'success');
      setShowSignatureDrawer(false);

      // Reintentar firma digital
      await actasAPI.firmarDigital({
        asignacionId: asignacion?.id,
        movimientoId: movimiento?.id,
        observaciones: 'Firma digital aplicada (firma creada desde modal)',
      });
      showToast('Éxito', 'Acta firmada digitalmente correctamente', 'success');
      if (onActionComplete) onActionComplete();
    } catch (e) {
      console.error('Error guardando firma o firmando acta:', e);
      showToast('Error', 'No se pudo guardar la firma o firmar el acta', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Función para subir PDF
  const handleUploadPdf = async (file) => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('acta', file);
      if (asignacion) formData.append('asignacionId', asignacion.id);
      if (movimiento) formData.append('movimientoId', movimiento.id);
      formData.append('observaciones', 'PDF firmado subido por el usuario');

      await actasAPI.subirPdf(formData);
      showToast('Éxito', 'PDF subido correctamente', 'success');
      if (onActionComplete) onActionComplete();
    } catch (error) {
      console.error('Error al subir PDF:', error);
      showToast('Error', 'No se pudo subir el PDF', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Función para aprobar acta
  const handleApprove = () => {
    if (onApprove) {
      onApprove();
    } else {
      // Fallback: hacer la acción directamente
      handleApproveDirect();
    }
  };

  // Función para rechazar acta
  const handleReject = () => {
    if (onReject) {
      onReject();
    } else {
      // Fallback: hacer la acción directamente
      handleRejectDirect();
    }
  };

  // Marcar como pendiente de firma (disponible en cualquier estado)
  const handleMarkPending = async () => {
    try {
      setLoading(true);
      if (acta && acta.id) {
        await actasAPI.marcarPendiente(acta.id);
      } else {
        await actasAPI.marcarPendienteFirma({
          asignacionId: asignacion?.id,
          movimientoId: movimiento?.id
        });
      }
      showToast('Éxito', 'Acta marcada como pendiente de firma', 'success');
      if (onActionComplete) onActionComplete();
    } catch (error) {
      console.error('Error al marcar pendiente:', error);
      showToast('Error', 'No se pudo marcar como pendiente de firma', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Función para aprobar acta directamente (fallback)
  const handleApproveDirect = async () => {
    try {
      setLoading(true);
      await actasAPI.aprobarActa(acta.id, { aprobar: true, comentarios: '' });
      showToast('Éxito', 'Acta aprobada correctamente', 'success');
      if (onActionComplete) onActionComplete();
    } catch (error) {
      console.error('Error al aprobar:', error);
      showToast('Error', 'No se pudo aprobar el acta', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Función para rechazar acta directamente (fallback)
  // TODO: reemplazar window.prompt con ReasonModal (mismo patrón que handleReject)
  const handleRejectDirect = async () => {
    try {
      setLoading(true);
      // eslint-disable-next-line no-alert
      const motivo = window.prompt('Escribe el motivo de rechazo:');
      if (!motivo || !motivo.trim()) {
        showToast('Información', 'Debes ingresar un motivo de rechazo', 'info');
        return;
      }
      await actasAPI.rechazarActa(acta.id, { motivo });
      showToast('Éxito', 'Acta rechazada correctamente', 'success');
      if (onActionComplete) onActionComplete();
    } catch (error) {
      console.error('Error al rechazar:', error);
      showToast('Error', 'No se pudo rechazar el acta', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Rechazo con modal de motivo (TI)
  const handleRejectDirectWithReason = async (motivo) => {
    try {
      setLoading(true);
      await actasAPI.rechazarActa(acta.id, { motivo: (motivo || '').trim() });
      showToast('Éxito', 'Acta rechazada correctamente', 'success');
      if (onActionComplete) onActionComplete();
    } catch (error) {
      console.error('Error al rechazar:', error);
      showToast('Error', 'No se pudo rechazar el acta', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Función para aprobar acta para TI (permite comentario opcional si no hay PDF usuario)
  const handleApproveTI = async (comentarios = '') => {
    try {
      setLoading(true);
      await actasAPI.aprobarActa(acta.id, { aprobar: true, comentarios: comentarios || '' });
      showToast('Éxito', 'Acta aprobada correctamente', 'success');
      if (onActionComplete) onActionComplete();
    } catch (error) {
      console.error('Error al aprobar:', error);
      showToast('Error', 'No se pudo aprobar el acta', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Anular acta
  const handleAnnul = async (motivo) => {
    try {
      if (!acta || !acta.id) return;
      setLoading(true);
      await actasAPI.anularActa(acta.id, { motivo: (motivo || '').trim() });
      showToast('Éxito', 'Acta anulada', 'success');
      if (onActionComplete) onActionComplete();
    } catch (error) {
      console.error('Error al anular:', error);
      showToast('Error', 'No se pudo anular el acta', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {/* Botón de Previsualizar */}
      {canPreview && (
        <Tooltip content="Previsualizar acta según su estado actual">
          <button
            onClick={handlePreview}
            disabled={loading}
            className="flex items-center space-x-2 px-3 py-2 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-800/50 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-all shadow-sm font-medium disabled:opacity-50"
          >
            <Eye className="w-4 h-4" />
            <span>Previsualizar</span>
          </button>
        </Tooltip>
      )}

      {/* Botón de Descargar */}
      {canDownload && (
        <Tooltip content="Descargar acta">
          <button
            onClick={handleDownload}
            disabled={loading}
            className="flex items-center space-x-2 px-3 py-2 bg-slate-50 dark:bg-gray-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-100 dark:hover:bg-gray-700 transition-all shadow-sm font-medium disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            <span>Descargar</span>
          </button>
        </Tooltip>
      )}

      {/* Botón de Firma Digital (solo usuarios) */}
      {canSign && currentUserRole !== 'admin' && currentUserRole !== 'soporte' && (
        <Tooltip content="Firmar digitalmente">
          <button
            onClick={handleDigitalSign}
            disabled={loading}
            className="flex items-center space-x-2 px-3 py-2 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-800/50 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-all shadow-sm font-medium disabled:opacity-50"
          >
            <PenTool className="w-4 h-4" />
            <span>Firmar Digital</span>
          </button>
        </Tooltip>
      )}

      {/* Botón de Subir PDF (usuario) */}
      {canUpload && currentUserRole !== 'admin' && currentUserRole !== 'soporte' && (
        <Tooltip content="Subir PDF firmado manualmente">
          <label className="flex items-center space-x-2 px-3 py-2 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-800/50 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-all shadow-sm font-medium disabled:opacity-50 cursor-pointer">
            <Upload className="w-4 h-4" />
            <span>Subir PDF</span>
            <input
              type="file"
              accept=".pdf"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  handleUploadPdf(file);
                }
              }}
              className="hidden"
              disabled={loading}
            />
          </label>
        </Tooltip>
      )}

      {/* Botón de Subir PDF (TI/Admin) */}
      {currentUserRole && (currentUserRole === 'admin' || currentUserRole === 'soporte') && (
        <Tooltip content="Subir PDF firmado en nombre de TI">
          <label className="flex items-center space-x-2 px-3 py-2 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-800/50 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-all shadow-sm font-medium disabled:opacity-50 cursor-pointer">
            <Upload className="w-4 h-4" />
            <span>Subir PDF (TI)</span>
            <input
              type="file"
              accept=".pdf"
              onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;
                try {
                  setLoading(true);
                  const formData = new FormData();
                  formData.append('acta', file);
                  if (asignacion) formData.append('asignacionId', String(asignacion.id));
                  if (movimiento) formData.append('movimientoId', String(movimiento.id));
                  formData.append('observaciones', 'PDF subido por TI');
                  await actasAPI.subirActaAdmin(formData);
                  showToast('Éxito', 'PDF subido por TI', 'success');
                  if (onActionComplete) onActionComplete();
                } catch (err) {
                  console.error('Error al subir PDF TI:', err);
                  showToast('Error', 'No se pudo subir el PDF TI', 'error');
                } finally {
                  setLoading(false);
                }
              }}
              className="hidden"
              disabled={loading}
            />
          </label>
        </Tooltip>
      )}

      {/* Botón de Marcar Pendiente (siempre visible para TI) */}
      <Tooltip content="Marcar acta como pendiente de firma en cualquier estado">
        <button
          onClick={handleMarkPending}
          disabled={loading}
          className="flex items-center space-x-2 px-3 py-2 bg-slate-50 dark:bg-gray-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-100 dark:hover:bg-gray-700 transition-all shadow-sm font-medium disabled:opacity-50"
        >
          <Clock className="w-4 h-4" />
          <span>Marcar Pendiente</span>
        </button>
      </Tooltip>

      {/* Botón de Aprobar (TI siempre visible si existe acta) */}
      {(currentUserRole === 'admin' || currentUserRole === 'soporte') && acta?.id ? (
        <Tooltip content="Aprobar acta (TI)">
          <button
            onClick={() => setModals((m) => ({ ...m, approve: true }))}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-800/50 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-all shadow-sm font-medium disabled:opacity-50"
          >
            <CheckCircle className="w-4 h-4" />
            <span>Aprobar Acta</span>
          </button>
        </Tooltip>
      ) : (
        canApprove && (
          <Tooltip content="Aprobar acta">
            <button
              onClick={handleApprove}
              disabled={loading}
              className="flex items-center space-x-2 px-3 py-2 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-800/50 rounded-lg hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-all shadow-sm font-medium disabled:opacity-50"
            >
              <CheckCircle className="w-4 h-4" />
              <span>Aprobar</span>
            </button>
          </Tooltip>
        )
      )}

      {/* Botón de Rechazar (TI siempre visible si existe acta) */}
      {(currentUserRole === 'admin' || currentUserRole === 'soporte') && acta?.id ? (
        <Tooltip content="Rechazar acta (TI)">
          <button
            onClick={() => setModals((m) => ({ ...m, reject: true }))}
            disabled={loading}
            className="flex items-center space-x-2 px-3 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-100 dark:border-red-900/30 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/40 transition-all shadow-sm font-medium disabled:opacity-50"
          >
            <XCircle className="w-4 h-4" />
            <span>Rechazar</span>
          </button>
        </Tooltip>
      ) : (
        canReject && (
          <Tooltip content="Rechazar acta">
            <button
              onClick={handleReject}
              disabled={loading}
              className="flex items-center space-x-2 px-3 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-100 dark:border-red-900/30 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/40 transition-all shadow-sm font-medium disabled:opacity-50"
            >
              <XCircle className="w-4 h-4" />
              <span>Rechazar</span>
            </button>
          </Tooltip>
        )
      )}

      {/* Botón de Anular (TI) */}
      {(currentUserRole === 'admin' || currentUserRole === 'soporte') && acta?.id && (
        <Tooltip content="Anular acta">
          <button
            onClick={() => setModals((m) => ({ ...m, annul: true }))}
            disabled={loading}
            className="flex items-center space-x-2 px-3 py-2 bg-slate-50 dark:bg-gray-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-100 dark:hover:bg-gray-700 transition-all shadow-sm font-medium disabled:opacity-50"
          >
            <XCircle className="w-4 h-4" />
            <span>Anular</span>
          </button>
        </Tooltip>
      )}

      {/* Modales TI */}
      <ReasonModal
        isOpen={modals.approve}
        title="Aprobar acta"
        label="Comentario (opcional)"
        placeholder="Comentario de aprobación..."
        confirmText="Aprobar"
        required={false}
        onConfirm={(comentarios) => {
          setModals((m) => ({ ...m, approve: false }));
          handleApproveTI(comentarios);
        }}
        onClose={() => setModals((m) => ({ ...m, approve: false }))}
      />

      <ReasonModal
        isOpen={modals.reject}
        title="Rechazar acta"
        label="Motivo"
        placeholder="Describe el motivo del rechazo..."
        confirmText="Rechazar"
        required={false}
        onConfirm={async (motivo) => {
          setModals((m) => ({ ...m, reject: false }));
          await handleRejectDirectWithReason(motivo);
        }}
        onClose={() => setModals((m) => ({ ...m, reject: false }))}
      />

      <ReasonModal
        isOpen={modals.annul}
        title="Anular acta"
        label="Motivo"
        placeholder="Describe el motivo de anulación..."
        confirmText="Anular"
        required={false}
        onConfirm={(motivo) => {
          setModals((m) => ({ ...m, annul: false }));
          handleAnnul(motivo);
        }}
        onClose={() => setModals((m) => ({ ...m, annul: false }))}
      />

      {/* Indicador de carga */}
      {loading && (
        <div className="flex items-center space-x-2 px-3 py-2 bg-gray-100 text-gray-600 rounded-lg">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          <span>Procesando...</span>
        </div>
      )}
      {/* Modal para crear firma cuando no existe */}
      <SignatureDrawer
        isOpen={showSignatureDrawer}
        onClose={() => setShowSignatureDrawer(false)}
        onSave={handleSaveSignature}
        title="Crear firma digital"
      />
    </div>
  );
};

export default ActaActions;
